import type {
  VehicleInsuranceSaveRequestV2,
  VehicleSaveRequestV2,
  VehicleSectionV2,
} from "@credit-acceptance/step6-bff-client";
import { z } from "zod";

// @TODO: I copied the setup in Signers.schema.ts but is it okay that this is not using i18n for the validation messages?
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const ODOMETER_DISC_MILEAGE_LENGTH_MESSAGE = "Must be 7 characters or fewer";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const NUMBERS_ONLY_MESSAGE = "Numbers only";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const LIC_PLATE_LENGTH_MESSAGE = "Must be 30 characters or fewer";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const NON_NEGATIVE_MESSAGE = "Must be zero or greater";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const CPO_DATE_REQUIRED_MESSAGE = "This is a required field.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const CPO_DATE_FUTURE_MESSAGE = "Must not be a future date";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const CPO_DATE_INVALID_MESSAGE = "CPO date is invalid (MM/DD/YYYY).";

/** Spec §5.3: a CPO date more than this many days after the contract date warns, but never blocks the save. */
export const CPO_DATE_CONTRACT_GRACE_DAYS = 5;

/**
 * Whether an ISO `YYYY-MM-DD` string is a real calendar date. The shape check alone would accept
 * `2023-02-31`, so the parsed date is round-tripped back to ISO and compared.
 * @param {string} isoDate - The candidate `YYYY-MM-DD` string.
 * @returns {boolean} True when the date exists.
 */
export function isRealIsoDate(isoDate: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return false;
  }
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(isoDate);
}

/**
 * Local-midnight-safe ISO `YYYY-MM-DD` for "today", mirroring dateInput.tsx's
 * `dateToIso` (not exported there, so duplicated here rather than reaching
 * into that module — same as Signers.schema.ts not sharing date logic with it).
 * @returns {string} Today's date as `YYYY-MM-DD`.
 */
function todayIso(): string {
  const now = new Date();
  const year = String(now.getFullYear()).padStart(4, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// ---------------------------------------------------------------------------
// contractVehicleSchema — spike scope only: the fields that already use the
// dual-mode TextField/CurrencyField/DateField/CheckboxField (Odometer Disc
// Mileage, Lic Plate #, Total Selling Price, Certified Pre-Owned, CPO Date).
// Year/Make/Model/VIN/Color/Book Wholesale/Book Retail stay LabeledValue-driven,
// outside this schema.
// ---------------------------------------------------------------------------
export const ODOMETER_DISC_MILEAGE_MAX_LENGTH = 7;
export const LIC_PLATE_MAX_LENGTH = 30;
export const TOTAL_SELLING_PRICE_MAX_LENGTH = 9;

export const contractVehicleSchema = z
  .object({
    odometerDiscMileage: z
      .string()
      .max(ODOMETER_DISC_MILEAGE_MAX_LENGTH, ODOMETER_DISC_MILEAGE_LENGTH_MESSAGE)
      .regex(/^\d*$/, NUMBERS_ONLY_MESSAGE),
    licPlate: z.string().max(LIC_PLATE_MAX_LENGTH, LIC_PLATE_LENGTH_MESSAGE),
    // CurrencyField's onChange already emits `number | undefined` for an empty
    // input (see CurrencyInput's parseRawInput) — no emptyStringToUndefined
    // preprocessing needed here, unlike Signers' text-based number inputs.
    totalSellingPrice: z.number().nonnegative(NON_NEGATIVE_MESSAGE).optional(),
    // Never user-editable and never validated — just reflects whatever drives
    // CPO Date's visibility (see the superRefine below).
    isCertifiedPreOwned: z.boolean(),
    cpoDate: z.string(),
  })
  .superRefine((data, context) => {
    if (!data.isCertifiedPreOwned) {
      return;
    }
    if (!data.cpoDate) {
      context.addIssue({ code: "custom", path: ["cpoDate"], message: CPO_DATE_REQUIRED_MESSAGE });
      return;
    }
    if (!isRealIsoDate(data.cpoDate)) {
      context.addIssue({ code: "custom", path: ["cpoDate"], message: CPO_DATE_INVALID_MESSAGE });
      return;
    }
    if (data.cpoDate > todayIso()) {
      context.addIssue({ code: "custom", path: ["cpoDate"], message: CPO_DATE_FUTURE_MESSAGE });
    }
    // The remaining rule in spec 5.3 — more than CPO_DATE_CONTRACT_GRACE_DAYS after the contract
    // date — is deliberately not a Zod issue: it warns without blocking the save, so it lives in
    // `exceedsContractDateGrace` and is surfaced by the component instead.
  });

export type ContractVehicleInput = z.infer<typeof contractVehicleSchema>;

/**
 * Narrows the Contract Vehicle form values to the three numeric fields the BFF actually persists.
 * The form carries display-only fields (Lic Plate, CPO) and keeps Odometer Disc Mileage as a string
 * so its "numbers only" / max-length rules can run on the raw input, so the values can't be handed
 * to the save endpoint as-is. An empty mileage is sent as `undefined`, which the BFF documents as
 * "leaves it unchanged" rather than clearing the stored odometer.
 * @param {ContractVehicleInput} values - The current form values.
 * @returns {VehicleSaveRequestV2} The save payload.
 */
export function toVehicleSaveRequest(values: ContractVehicleInput): VehicleSaveRequestV2 {
  const mileage = stripMileageSeparators(values.odometerDiscMileage);
  return {
    msrp: values.totalSellingPrice,
    odometerDiscMileage: mileage ? Number(mileage) : undefined,
  };
}

/**
 * Narrows the Contract Vehicle form values to the one field the BFF persists through its *insurance*
 * save leg: Lic Plate, which lives on CAS applicant data and so has no slot on `VehicleSaveRequestV2`.
 * Uppercased to match caps2, which both uppercases the plate on load (`setVehicleInfoOnLoad`) and on
 * blur in `step6_vehicle.xhtml`. A blank plate is sent as `undefined`, which the BFF documents as
 * "leaves the stored plate unchanged" rather than clearing it.
 * @param {ContractVehicleInput} values - The current form values.
 * @returns {VehicleInsuranceSaveRequestV2} The insurance-leg save payload.
 */
export function toVehicleInsuranceSaveRequest(values: ContractVehicleInput): VehicleInsuranceSaveRequestV2 {
  const licPlate = values.licPlate.trim().toUpperCase();
  return { licPlate: licPlate || undefined };
}

/** 7 mileage digits plus the 2 thousands separators `formatMileageForDisplay` can insert. */
export const ODOMETER_DISC_MILEAGE_DISPLAY_MAX_LENGTH = ODOMETER_DISC_MILEAGE_MAX_LENGTH + 2;

/**
 * Renders raw mileage digits with thousands separators (spec §5: "shown with thousands separators").
 * Expects the digits to already be normalized by `normalizeMileageInput`, which is what guarantees
 * there are no leading zeros for the grouping to mangle into `0,001,234`.
 * @param {string} [digits] - The stored mileage, digits only.
 * @returns {string} The display value, e.g. `1,234,567`.
 */
export function formatMileageForDisplay(digits?: string): string {
  return (digits ?? "").replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Strips separators back to the digits the form stores and the save sends (spec §5: "commas stripped
 * on save"). Also drops any other stray non-digit, which is what keeps the input digits-only.
 * @param {string} [value] - A displayed or pasted mileage.
 * @returns {string} Digits only.
 */
export function stripMileageSeparators(value?: string): string {
  return (value ?? "").replaceAll(/\D/g, "");
}

/**
 * Normalizes what the user typed, pasted or dropped into the digits the form stores: separators and
 * any other non-digit removed, leading zeros dropped, and truncated to the field's digit budget.
 *
 * The truncation can't be left to the input's `maxLength`, which counts the *formatted* characters:
 * a paste is truncated by the browser to 9 characters, and 9 pasted digits would otherwise reach
 * form state and render as `123,456,789` — longer than the field's own limit — until blur surfaced
 * the schema error.
 * @param {string} [value] - The raw input value.
 * @returns {string} Normalized digits, at most `ODOMETER_DISC_MILEAGE_MAX_LENGTH` of them.
 */
export function normalizeMileageInput(value?: string): string {
  return (
    stripMileageSeparators(value)
      // Only when another digit follows, so a lone "0" is still typeable.
      .replace(/^0+(?=\d)/, "")
      .slice(0, ODOMETER_DISC_MILEAGE_MAX_LENGTH)
  );
}

/**
 * Spec §5.3's non-blocking CPO warning: the certification date is more than
 * `CPO_DATE_CONTRACT_GRACE_DAYS` after the contract date. Returns false whenever either date is
 * missing or unparseable, so an absent contract date simply doesn't warn.
 * @param {string} [cpoDate] - The CPO certification date.
 * @param {string} [contractDate] - `financial.contractDate` from the dashboard response.
 * @returns {boolean} True when the grace period is exceeded.
 */
export function exceedsContractDateGrace(cpoDate?: string, contractDate?: string): boolean {
  const cpo = toIsoDate(cpoDate);
  const contract = toIsoDate(contractDate);
  // `toIsoDate` only checks the shape, so a real-calendar check is still needed: an out-of-range
  // contract date like `2023-13-01` would otherwise parse to an Invalid Date whose `toISOString()`
  // throws — out of a render, taking the whole section down — and `2023-02-31` would silently roll
  // over to Mar 2 and measure the grace window from the wrong day.
  if (!isRealIsoDate(cpo) || !isRealIsoDate(contract)) {
    return false;
  }
  // UTC arithmetic: `setUTCDate` rolls months/years over correctly and can't be shifted by the
  // local zone the way a local-midnight Date can.
  const limit = new Date(`${contract}T00:00:00Z`);
  limit.setUTCDate(limit.getUTCDate() + CPO_DATE_CONTRACT_GRACE_DAYS);
  return cpo > limit.toISOString().slice(0, 10);
}

/**
 * Whether a CPO date would fail `contractVehicleSchema` outright. Used to suppress the non-blocking
 * contract-date warning while a blocking error is in play, without depending on whether RHF has
 * happened to run a validation pass yet (it holds no errors at all until the first one).
 * @param {string} [cpoDate] - The CPO certification date.
 * @returns {boolean} True when the date is empty, not a real date, or in the future.
 */
export function hasBlockingCpoDateError(cpoDate?: string): boolean {
  return !cpoDate || !isRealIsoDate(cpoDate) || cpoDate > todayIso();
}

/**
 * Spec §5.1: the VIN label is a link to the NADA popup only when the deal is NADA-eligible *and* the
 * vehicle is not new. The BFF's `nadaEligible` only covers the first two of the rule's three terms —
 * its own docs scope it to "the dealer's state has an active NADAGAPLTV rule and the deal has GAP" —
 * so the `isNew != 'Y'` term has to be applied here.
 * @param {VehicleSectionV2} [vehicle] - The dashboard vehicle section.
 * @param {string} [fallbackCondition] - `financial.vehicle.condition`.
 * @returns {boolean} True when the VIN label should be a link.
 */
export function isNadaLinkEligible(vehicle?: VehicleSectionV2, fallbackCondition?: string): boolean {
  return (
    vehicle?.vehicleOnDeal?.nadaEligible === true && !isNewVehicle(resolveVehicleCondition(vehicle, fallbackCondition))
  );
}

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/**
 * Trims a deal timestamp (e.g. `2023-06-15T00:00:00Z`) to its `YYYY-MM-DD` date part. Deliberately
 * string-based rather than `new Date()`, which parses as UTC and can shift the day.
 * @param {string | undefined} timestamp - Timestamp passed through from the deal.
 * @returns {string} The ISO date, or `""` when absent or not date-shaped.
 */
export function toIsoDate(timestamp?: string): string {
  return ISO_DATE_PREFIX.exec(timestamp ?? "")?.[0] ?? "";
}

/**
 * Whether the vehicle condition is NEW (case- and whitespace-insensitive). Anything else — including
 * `CPO`, which the deal reports as its own condition value rather than as a flavour of NEW, and absent —
 * is not.
 * @param {string | undefined} condition - The resolved vehicle condition (see `resolveVehicleCondition`).
 * @returns {boolean} True for a new vehicle.
 */
export function isNewVehicle(condition?: string): boolean {
  return condition?.trim().toUpperCase() === "NEW";
}

/**
 * The vehicle condition to display and gate on: `New`, `Used` or `CPO`. Prefers the deal's own
 * `vehicleOnDeal.condition` (spec §5's source of truth for the Vehicle tab) and falls back to the
 * `financial.vehicle.condition` the section is handed, which is the same value read via a different
 * leg of the dashboard response and is all that's available on deals with no vehicle line.
 * @param {VehicleSectionV2} [vehicle] - The dashboard vehicle section.
 * @param {string} [fallback] - `financial.vehicle.condition`.
 * @returns {string | undefined} The condition, or undefined when neither source has one.
 */
export function resolveVehicleCondition(vehicle?: VehicleSectionV2, fallback?: string): string | undefined {
  return vehicle?.vehicleOnDeal?.condition?.trim() || fallback?.trim() || undefined;
}

/**
 * Whether the deal's vehicle is certified pre-owned — spec §5.3 renders the CPO checkbox + date row
 * `iff cpo`. Accepts any of the three equivalent signals the dashboard can carry: the explicit
 * `vehicleOnDeal.cpo` flag, a `CPO` condition, or the presence of the CPO details block (which is what
 * the BFF derives `cpo` from in the first place, so the row still shows if only one of them arrives).
 * @param {VehicleSectionV2} [vehicle] - The dashboard vehicle section.
 * @returns {boolean} True for a CPO vehicle.
 */
export function isCpoVehicle(vehicle?: VehicleSectionV2): boolean {
  const onDeal = vehicle?.vehicleOnDeal;
  return (
    onDeal?.cpo === true ||
    onDeal?.condition?.trim().toUpperCase() === "CPO" ||
    Boolean(onDeal?.certifiedPreOwnedDetails)
  );
}

/**
 * Seeds the Contract Vehicle form from the V2 dashboard `vehicle` section. CPO Date is prefilled only
 * when the certification is verified (spec 5.3); the CPO row shows whenever the deal reports a CPO
 * vehicle (see `isCpoVehicle`), even if its certification details haven't been filled in yet.
 * @param {VehicleSectionV2} [vehicle] - The dashboard vehicle section.
 * @returns {ContractVehicleInput} Default form values.
 */
export function buildContractVehicleDefaultValues(vehicle?: VehicleSectionV2): ContractVehicleInput {
  const onDeal = vehicle?.vehicleOnDeal;
  const cpo = onDeal?.certifiedPreOwnedDetails;
  return {
    // `?? ""` / `?? undefined`: the BFF serializes VehicleOnDealV2's optionals as explicit JSON nulls,
    // which would otherwise stringify to "null" here and fail `z.number().optional()` below.
    odometerDiscMileage: String(onDeal?.odometerMileage ?? ""),
    // Uppercased on load for caps2 parity: `setVehicleInfoOnLoad` stores
    // `step6VehicleContractDetailWO.getLicPlate().toUpperCase()`, so the plate is already uppercase
    // before the user ever focuses the field (the input's own blur handler only covers what they type).
    licPlate: vehicle?.licensePlateNo?.toUpperCase() ?? "",
    totalSellingPrice: onDeal?.msrp ?? undefined,
    isCertifiedPreOwned: isCpoVehicle(vehicle),
    cpoDate: cpo?.isVerified ? toIsoDate(cpo.certificationDate) : "",
  };
}
